import type { ReactNode } from "react";
import {
	isRouteErrorResponse,
	useRouteError,
	useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";
import { Button } from "@/components/ui";
import { ROUTES } from "@/app/routes";

interface ErrorLayoutProps {
	children?: ReactNode;
	errorCode?: string | number;
	title?: string;
	message?: string;
	className?: string;
}

const ErrorLayout = ({
	children,
	errorCode,
	title,
	message,
	className,
}: ErrorLayoutProps) => {
	const error = useRouteError();
	const navigate = useNavigate();
	const { t } = useTranslation();

	// Determine error details
	let displayCode = errorCode;
	let displayTitle = title;
	let displayMessage = message;

	if (!displayCode && isRouteErrorResponse(error)) {
		displayCode = error.status;
		displayTitle = displayTitle || error.statusText;
		displayMessage = displayMessage || error.data;
	} else if (!displayCode && error instanceof Error) {
		displayCode = "500";
		displayTitle = displayTitle || t("error.internal_error");
		displayMessage = displayMessage || t("error.internal_error_message");
	}

	// Default values with translations
	displayCode = displayCode || "404";
	displayTitle = displayTitle || t("error.page_not_found");
	displayMessage = displayMessage || t("error.page_not_found_message");

	return (
		<div
			className={cn(
				"min-h-screen flex items-center justify-center bg-gradient-to-b from-bg-secondary to-bg-tertiary px-4",
				className,
			)}
		>
			<div className="w-full max-w-[var(--modal-width-lg)]">
				{children || (
					<div className="bg-bg-primary rounded-lg shadow-sm overflow-hidden">
						<div className="p-6">
							<div className="flex items-center gap-6">
								{/* Error Code */}
								<div className="flex-shrink-0">
									<div className="text-7xl font-black text-muted-inverse select-none">
										{displayCode}
									</div>
								</div>

								{/* Error Details */}
								<div className="flex-1">
									<h1 className="text-xl font-bold text-text-primary mb-1">
										{displayTitle}
									</h1>
									<p className="text-xs text-text-secondary mb-3">
										{displayMessage}
									</p>

									{/* Actions */}
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => navigate(-1)}
										>
											{t("error.go_back")}
										</Button>
										<Button
											variant="primary"
											size="sm"
											onClick={() => navigate(ROUTES.DASHBOARD)}
										>
											{t("error.go_to_dashboard")}
										</Button>
									</div>
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="bg-bg-secondary px-6 py-3 border-t border-border-tertiary">
							<p className="text-xs text-text-tertiary">
								{t("error.support_message")}{" "}
								<a
									href="mailto:support@hulahoop.media"
									className="text-text-secondary hover:text-text-primary underline"
								>
									support@hulahoop.media
								</a>
							</p>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ErrorLayout;
